// Per-case timeline view mode preference. Stored in localStorage under
// a case-scoped key so the user's choice in the UV case doesn't bleed
// into their Bank Dispute case. Falls back to a size-based default
// (Comfortable for small cases, Compact for big ones) when the user
// has never set one.

export type ViewMode = "compact" | "comfortable" | "reading";
// Timeline tab now hosts two sub-views: the visual infographic
// (inherited from the old Timeline tab) and the written prose
// narrative (inherited from the retired Story tab). Persisted per
// case so the choice carries across sessions and reloads.
export type TimelineSubView = "visual" | "written";

const KEY_PREFIX = "casethread:view-mode:";
const ROUTINE_KEY_PREFIX = "casethread:show-routine:";
const TIMELINE_SUBVIEW_KEY_PREFIX = "casethread:timeline-subview:";
const CHRONO_EXPANDED_KEY_PREFIX = "casethread:chrono-expanded:";
const SECTION_EXPANDED_KEY_PREFIX = "casethread:section-expanded:";

export function defaultViewMode(recordCount: number): ViewMode {
  return recordCount > 15 ? "compact" : "comfortable";
}

export function loadViewMode(caseId: string): ViewMode | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + caseId);
    if (raw === "compact" || raw === "comfortable" || raw === "reading") {
      return raw;
    }
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

export function saveViewMode(caseId: string, mode: ViewMode): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + caseId, mode);
  } catch {
    /* ignore */
  }
}

export function loadShowRoutine(caseId: string): boolean {
  try {
    const raw = window.localStorage.getItem(ROUTINE_KEY_PREFIX + caseId);
    return raw === "1";
  } catch {
    return false;
  }
}

export function saveShowRoutine(caseId: string, show: boolean): void {
  try {
    window.localStorage.setItem(ROUTINE_KEY_PREFIX + caseId, show ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// Chronology date-group expansion (Round 9): per-case map of EXPLICIT user
// toggles, keyed by the group's date string. Groups absent from the map use
// the size-based default (collapsed when the case exceeds the threshold).
export function loadChronoExpanded(caseId: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(CHRONO_EXPANDED_KEY_PREFIX + caseId);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const out: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>))
          if (typeof v === "boolean") out[k] = v;
        return out;
      }
    }
  } catch {
    /* localStorage unavailable / corrupt entry */
  }
  return {};
}

export function saveChronoExpanded(
  caseId: string,
  map: Record<string, boolean>,
): void {
  try {
    window.localStorage.setItem(
      CHRONO_EXPANDED_KEY_PREFIX + caseId,
      JSON.stringify(map),
    );
  } catch {
    /* ignore */
  }
}

// Overview section expansion (Round 13, Phase 1): per-case map of EXPLICIT
// user toggles keyed by section id (ov-financial, ov-commitments, …). Every
// section below "Needs attention" defaults COLLAPSED to a counted summary
// row; absence from the map means collapsed. Mirrors loadChronoExpanded.
export function loadSectionExpanded(caseId: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(SECTION_EXPANDED_KEY_PREFIX + caseId);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const out: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>))
          if (typeof v === "boolean") out[k] = v;
        return out;
      }
    }
  } catch {
    /* localStorage unavailable / corrupt entry */
  }
  return {};
}

export function saveSectionExpanded(
  caseId: string,
  map: Record<string, boolean>,
): void {
  try {
    window.localStorage.setItem(
      SECTION_EXPANDED_KEY_PREFIX + caseId,
      JSON.stringify(map),
    );
  } catch {
    /* ignore */
  }
}

export function loadTimelineSubView(caseId: string): TimelineSubView {
  try {
    const raw = window.localStorage.getItem(
      TIMELINE_SUBVIEW_KEY_PREFIX + caseId,
    );
    if (raw === "visual" || raw === "written") return raw;
  } catch {
    /* localStorage unavailable */
  }
  return "visual";
}

export function saveTimelineSubView(
  caseId: string,
  subView: TimelineSubView,
): void {
  try {
    window.localStorage.setItem(
      TIMELINE_SUBVIEW_KEY_PREFIX + caseId,
      subView,
    );
  } catch {
    /* ignore */
  }
}
